import { useCallback, useEffect } from "react";
import { makeVar, ReactiveVar, useReactiveVar } from "@apollo/client";

/*
What the reader has open while they talk to Ollie. The panel floats over somebody else's screen,
so without this a question like "why did this fail?" reaches the model with no subject: it sees the
words, not the report behind the panel.

It is a sentence rather than a set of ids because that is what reaches the model — one line naming
the record and carrying the ids it would need to look it up. It travels in the prompt, never in the
message, so it is not quoted back at the reader as text they did not write.
*/

const ollieScreenDescriptionInitialValue = "";

export const ollieScreenDescriptionVar: ReactiveVar<string> = makeVar<string>(ollieScreenDescriptionInitialValue);

export const clearOllieScreenDescription = () => ollieScreenDescriptionVar(ollieScreenDescriptionInitialValue);

/*
The records behind the screen, as ids rather than words. A new chat reads its jurisdiction content
from them: the jurisdiction on the screen, or the ones behind the property on it. A screen with
neither leaves the chat on every property the reader has.
*/
export interface OllieScreenRecords {
	propertyId?: string | null;
	jurisdictionId?: string | null;
}

const ollieScreenRecordsInitialValue: OllieScreenRecords = {};

export const ollieScreenRecordsVar: ReactiveVar<OllieScreenRecords> = makeVar<OllieScreenRecords>(
	ollieScreenRecordsInitialValue
);

/*
Tells Ollie what this screen is showing, for as long as it is mounted. Returning to a screen with
nothing in particular open clears it, so a stale record never follows the reader around.

Call it with the sentence itself, not with a memo of it: the effect re-runs on the sentence, and
two identical sentences are the same string, so a screen that re-renders without changing what it
shows never touches the var.

Pass an empty string while the data is still loading — an id with no name attached tells the model
less than saying nothing, and the reader can ask before the page has finished loading.
*/
export const useDescribeScreenToOllie = (
	screenDescription: string,
	{ propertyId = null, jurisdictionId = null }: OllieScreenRecords = {}
) => {
	useEffect(() => {
		const records = { propertyId, jurisdictionId };

		ollieScreenDescriptionVar(screenDescription);
		ollieScreenRecordsVar(records);

		return () => {
			// Only if nothing else claimed it in the meantime: React mounts the next screen before
			// it unmounts this one, and clearing unconditionally would wipe the new screen's description.
			if (ollieScreenDescriptionVar() === screenDescription) {
				clearOllieScreenDescription();
			}

			if (ollieScreenRecordsVar() === records) {
				ollieScreenRecordsVar(ollieScreenRecordsInitialValue);
			}
		};
	}, [screenDescription, propertyId, jurisdictionId]);
};

// Reads what the screens described. The chat sends it with every question.
export const useOllieScreenDescription = () => {
	const ollieScreenDescriptionValue = useReactiveVar(ollieScreenDescriptionVar);
	const ollieScreenRecordsValue = useReactiveVar(ollieScreenRecordsVar);

	const updateOllieScreenDescription = useCallback((screenDescription: string) => {
		ollieScreenDescriptionVar(screenDescription);
	}, []);

	return { ollieScreenDescriptionValue, ollieScreenRecordsValue, updateOllieScreenDescription };
};
